import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { isNative } from "@/lib/capacitor";

export type PhotoResult = { dataUrl: string; mimeType: string };

export { isNative };

/**
 * Open the native camera, photo library, or a chooser dialog on iOS.
 *
 * - "prompt" (default): iOS shows its native "Take Photo / Choose from Library"
 *   chooser. Right for the standard add-photo flow where collectors might want
 *   either path.
 * - "camera": opens the camera directly.
 * - "library": opens the photo library directly.
 *
 * Falls back gracefully on web — caller should use a hidden <input type="file">
 * instead (with `capture="environment"` to bias toward camera).
 */
export async function pickNativePhoto(
  source: "camera" | "library" | "prompt" = "prompt",
): Promise<PhotoResult | null> {
  try {
    const image = await Camera.getPhoto({
      quality: 90,
      // Cap the longest edge at 1500px. iPhone shoots 4032×3024 which
      // bloats upload + Anthropic-side preprocessing without buying any
      // forensic detail — Sonnet downsamples internally anyway. Drops
      // payload roughly 60-70% per photo.
      width: 1500,
      allowEditing: false,
      resultType: CameraResultType.DataUrl,
      source:
        source === "camera" ? CameraSource.Camera
        : source === "library" ? CameraSource.Photos
        : CameraSource.Prompt,
      correctOrientation: true,
    });
    if (!image.dataUrl) return null;
    return { dataUrl: image.dataUrl, mimeType: image.format === "png" ? "image/png" : "image/jpeg" };
  } catch (e) {
    // User cancelled — not an error
    if (String(e).includes("cancelled") || String(e).includes("canceled") || String(e).includes("dismiss")) return null;
    throw e;
  }
}

/** Convert a dataUrl to a File object so existing upload logic stays unchanged. */
export function dataUrlToFile(dataUrl: string, filename: string, mimeType: string): File {
  const arr = dataUrl.split(",");
  const bstr = atob(arr[1]);
  const u8arr = new Uint8Array(bstr.length);
  for (let i = 0; i < bstr.length; i++) u8arr[i] = bstr.charCodeAt(i);
  return new File([u8arr], filename, { type: mimeType });
}
